import { CustomConfig } from '../../config';
import { ILanguageProvider } from '../../dynamicWorkspace';
import { ITheme } from '../interfaces/iTheme';
import { ThemeProperties } from '../themeProperties';
import { ThemeVariables } from '../themeVariables';
import { ThemeVariablesManager } from '../themeVariablesManager';

/**
 * The Material 3 Compact theme.
 *
 * @export
 * @class Mat3CompactTheme
 * @implements {ITheme}
 */
export class Mat3CompactTheme implements ITheme {

    public id: string = 'material.three.compact';
    public name: string = 'Material 3 Compact';

    private document: Document;
    private themeProperties?: ThemeProperties;
    private readonly themeVariablesManager: ThemeVariablesManager;
    private readonly languageProvider: ILanguageProvider;

    private readonly dgColorDarkBlue = '#000085';
    private readonly dgColorWhite = '#ffffff';
    private readonly dgLightGray = '#eef0f3';


    /**
     * Creates an instance of Mat3CompactTheme.
     *
     * @param {Document} document The document reference.
     * @param {ThemeVariablesManager} themeVariablesManager The theme variables manager.
     * @param {ILanguageProvider} languageProvider The language provider.
     * @memberof Mat3CompactTheme
     */
    public constructor(document: Document, themeVariablesManager: ThemeVariablesManager, languageProvider: ILanguageProvider) {
        this.document = document;
        this.themeVariablesManager = themeVariablesManager;
        this.languageProvider = languageProvider;
    }


    /**
     * Applies the theme.
     *
     * @memberof Mat3CompactTheme
     */
    public apply(): void {

        // Set the default DevExpress behavior
        DevExpress.config({
            editorStylingMode: 'outlined'
        });

        this.themeVariablesManager.setVariableValue(ThemeVariables.navigationPanelIconMaxHeight, '32px');

        if (this.themeVariablesManager && this.themeProperties && this.themeProperties.currentViewStyle && this.themeProperties.currentViewStyle.colors) {
            const colorStyle = this.getStyleFromStyleClassName(this.themeProperties.currentViewStyle.colors);
            this.themeVariablesManager.setVariableValue(ThemeVariables.toolbarColor, colorStyle.backgroundColor);

            this.themeVariablesManager.setVariableValue(ThemeVariables.viewPrimaryColor, colorStyle.color);
            this.themeVariablesManager.setVariableValue(ThemeVariables.viewSecondaryColor, colorStyle.backgroundColor);
        }
    }

    /**
     * Updates the theme properties.
     *
     * @param {ThemeProperties} properties The properties to apply.
     * @memberof Mat3CompactTheme
     */
    public updateThemeProperties(properties: ThemeProperties): void {
        this.themeProperties = properties;
    }

    /**
     * Gets the computed custom config.
     *
     * @param {CustomConfig} customConfig The custom config to compute.
     * @return {CustomConfig} The computed custom config.
     * @memberof Mat3CompactTheme
     */
    public getComputedCustomConfig(customConfig: CustomConfig): CustomConfig {

        customConfig.activeViewBackgroundColor = this.dgColorDarkBlue;
        customConfig.activeViewColor = this.dgColorWhite;
        customConfig.activeViewIconColor = this.dgColorWhite;

        customConfig.headerBackgroundColor = this.dgColorDarkBlue;
        customConfig.headerIconHoverColor = this.dgColorDarkBlue;
        customConfig.headerTextColor = this.dgColorWhite;

        customConfig.navigationBackgroundColor = this.dgColorWhite;
        customConfig.navigationHoverBackgroundColor = this.dgLightGray;
        customConfig.navigationHoverColor = this.dgColorDarkBlue;
        customConfig.navigationTextColor = this.dgColorDarkBlue;

        customConfig.subTitle = this.languageProvider.get('framework.header.subTitle');
        customConfig.title = this.languageProvider.get('framework.header.title');
        customConfig.titleBackgroundColor = this.dgColorDarkBlue;
        customConfig.titleTextColor = this.dgColorWhite;
        customConfig.browserTitle = this.languageProvider.get('framework.header.headerTitle');

        customConfig.iconPath = './framework/assets/img/productIcon.svg';

        // At the moment view colors are fixed and they don't effect the theme
        // Use the current view style
        // if (this.themeProperties && this.themeProperties.currentViewStyle && this.themeProperties.currentViewStyle.colors) {
            // const colorStyle = this.getStyleFromStyleClassName(this.themeProperties.currentViewStyle.colors);
            // customConfig.navigationHoverColor = colorStyle.color;
            // customConfig.navigationHoverBackgroundColor = colorStyle.backgroundColor;
            // customConfig.navigationTextColor = colorStyle.backgroundColor;
            // customConfig.headerBackgroundColor = colorStyle.backgroundColor;
            // customConfig.headerTextColor = colorStyle.color;
            // customConfig.activeViewBackgroundColor = colorStyle.backgroundColor;
            // customConfig.activeViewColor = colorStyle.color;
            // customConfig.activeViewIconColor = colorStyle.color;
        // }

        return customConfig;
    }

    /**
     * Get the style from the style class name.
     *
     * @private
     * @param {string} styleClassName The style class name.
     * @return {CSSStyleDeclaration} The style.
     * @memberof Mat3CompactTheme
     */
    private getStyleFromStyleClassName(styleClassName: string): CSSStyleDeclaration {
        const tempElement = document.createElement('div');

        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.pointerEvents = 'none';
        tempElement.classList.add(styleClassName);

        this.document.body.appendChild(tempElement);
        const elementStyle = window.getComputedStyle(tempElement);
        const clonedStyleJson = JSON.stringify(elementStyle);
        this.document.body.removeChild(tempElement);

        return JSON.parse(clonedStyleJson);
    }

}